<template>
  <div
    class="create-product-container max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8"
  >
    <button
      @click="goBack"
      class="flex items-center text-gray-400 hover:text-blue-400 mb-6 transition-colors"
    >
      <span class="mr-2">&larr;</span> Nazad
    </button>

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-white mb-2">Dodaj novi oglas</h1>
      <p class="text-gray-400">
        Popunite sve potrebne informacije o proizvodu koji želite da prodate
      </p>
    </div>

    <!-- Loading za statistike -->
    <div v-if="isLoadingStats" class="mb-6">
      <div class="bg-gray-800 rounded-lg p-4 flex items-center justify-center">
        <div
          class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-3"
        ></div>
        <span class="text-gray-400">Proverava se broj aktivnih oglasa...</span>
      </div>
    </div>

    <!-- Upozorenja o limitu - JEDNOSTAVNIJA LOGIKA -->
    <div v-if="!isLoadingStats" class="mb-6">
      <!-- BLOKIRANJE - 0 preostalih oglasa -->
      <div
        v-if="userStats.remainingSlots === 0"
        class="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-6"
      >
        <div class="flex items-start">
          <svg
            class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <div>
            <h3 class="text-red-400 font-medium mb-1">
              Dostigli ste maksimalan broj aktivnih oglasa
            </h3>
            <p class="text-red-300 text-sm">
              Trenutno imate
              <strong>{{ userStats.activeProductsCount }}</strong> od
              <strong>{{ userStats.maxProductsAllowed }}</strong> dozvoljenih
              aktivnih oglasa. Da biste dodali novi oglas, sačekajte da neki
              postojeći istekne ili ga ručno obrišite.
            </p>
          </div>
        </div>
      </div>

      <!-- CRVENO UPOZORENJE - manje od 20 preostalih oglasa (ali ne 0) -->
      <div
        v-else-if="
          userStats.remainingSlots > 0 && userStats.remainingSlots < 20
        "
        class="bg-red-900/30 border border-red-500 rounded-lg p-4 mb-6"
      >
        <div class="flex items-start">
          <svg
            class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <div>
            <h3 class="text-red-400 font-medium mb-1">
              Kritično malo preostalih besplatnih oglasa
            </h3>
            <p class="text-red-300 text-sm">
              Možete dodati još samo
              <strong>{{ userStats.remainingSlots }}</strong> oglasa. Trenutno
              imate
              <strong>{{ userStats.activeProductsCount }}</strong> aktivnih
              oglasa.
            </p>
          </div>
        </div>
      </div>

      <!-- ŽUTO UPOZORENJE - između 20 i 39 preostalih oglasa -->
      <div
        v-else-if="
          userStats.remainingSlots >= 20 && userStats.remainingSlots < 40
        "
        class="bg-yellow-900/30 border border-yellow-500 rounded-lg p-4 mb-6"
      >
        <div class="flex items-start">
          <svg
            class="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <div>
            <h3 class="text-yellow-400 font-medium mb-1">
              Približavate se limitu maksimalnog broja besplatnih oglasa
            </h3>
            <p class="text-yellow-300 text-sm">
              Možete dodati još
              <strong>{{ userStats.remainingSlots }}</strong> oglasa. Trenutno
              imate
              <strong>{{ userStats.activeProductsCount }}</strong> aktivnih
              oglasa.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Forma -->
    <div class="bg-gray-800 shadow rounded-lg px-4 py-5 sm:p-6 overflow-hidden">
      <!-- OVERLAY kada je forma blokirana - SAMO kada je remainingSlots === 0 -->
      <div
        v-if="userStats.remainingSlots === 0 && !isLoadingStats"
        class="relative"
      >
        <div
          class="absolute inset-0 bg-gray-900/75 rounded-lg z-10 flex items-center justify-center"
        >
          <div class="text-center">
            <svg
              class="w-16 h-16 text-red-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <p class="text-red-400 font-medium">Forma je blokirana</p>
            <p class="text-red-300 text-sm mt-1">
              Dostigli ste maksimalan broj aktivnih oglasa
            </p>
          </div>
        </div>
        <div class="opacity-25 pointer-events-none">
          <ProductForm />
        </div>
      </div>

      <!-- NORMALNA FORMA kada može da se koristi -->
      <ProductForm v-else />
    </div>

    <!-- Toast notifikacija -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import ProductForm from '@/components/product/ProductForm.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import { useProductForm } from '@/composables/useProductForm';

export default defineComponent({
  name: 'CreateProductView',
  components: {
    ProductForm,
    ToastNotification,
  },
  setup() {
    const store = useStore();
    const router = useRouter();

    const {
      toast,
      userStats,
      isLoadingStats,
      canAddProduct,
      isAtLimit,
      loadUserStats,
    } = useProductForm();

    const isLoggedIn = computed(() => store.state.auth.isLoggedIn);

    onMounted(async () => {
      if (!isLoggedIn.value) {
        router.push('/login');
        return;
      }

      await loadUserStats();
    });

    const goBack = () => {
      router.back();
    };

    return {
      toast,
      userStats,
      isLoadingStats,
      canAddProduct,
      isAtLimit,
      goBack,
    };
  },
});
</script>

<style scoped>
.create-product-container {
  position: relative;
  width: 100%;
}

/* Smooth animations */
.opacity-25 {
  transition: opacity 0.3s ease-in-out;
}

.bg-red-900\/30,
.bg-yellow-900\/30 {
  transition: all 0.3s ease-in-out;
}
</style>
